import { useMemo } from 'react';
import { getCustomerId, getProjectId } from '../../utils/cookies';
import { usePortalProjectOptional } from '../../contexts/PortalProjectContext';
import { useResourcePermissionsStore } from '../../stores/zustand/resourcePermissionsStore';
import type { PermissionAction, PermissionContext } from '../types';
import {
  SCHEDULER_ACTION_PERMISSIONS,
  type SchedulerActionPermissionKey,
} from './actionPermissions';
import type { SchedulerNavPermission } from './resources';

export function usePermissionContext(): PermissionContext {
  const portal = usePortalProjectOptional();

  return useMemo(
    () => ({
      customerId: getCustomerId() || undefined,
      projectId: portal?.projectId || getProjectId() || undefined,
    }),
    [portal?.projectId]
  );
}

export function useCan(
  resourceId: string,
  action: PermissionAction = 'view',
  contextOverride?: Partial<PermissionContext>
) {
  const baseContext = usePermissionContext();
  const context = { ...baseContext, ...contextOverride };
  return useResourcePermissionsStore((s) => s.can(resourceId, action, context));
}

export function useCanSchedulerNav(nav: SchedulerNavPermission) {
  const context = usePermissionContext();
  return useResourcePermissionsStore((s) =>
    s.canShowNav(nav, {
      customerId: context.customerId,
      projectId: context.projectId,
    })
  );
}

export function useCanSchedulerAction(
  actionKey: SchedulerActionPermissionKey,
  contextOverride?: Partial<PermissionContext>
) {
  const context = usePermissionContext();
  const actionPermission = SCHEDULER_ACTION_PERMISSIONS[actionKey];
  const mergedContext = { ...context, ...contextOverride };

  return useResourcePermissionsStore((s) =>
    s.can(actionPermission.resourceId, actionPermission.action, {
      customerId: mergedContext.customerId,
      projectId: mergedContext.projectId,
    })
  );
}

export function useResourcePermissionsReady() {
  const status = useResourcePermissionsStore((s) => s.status);
  const error = useResourcePermissionsStore((s) => s.error);
  return {
    isReady: status === 'ready',
    isLoading: status === 'loading' || status === 'idle',
    error,
    status,
  };
}
